import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ShoppingCart, FileText, User, Building2, CheckCircle, XCircle, Clock } from 'lucide-react';
import API_BASE_URL from "../config";
import bellSound from '../SoundBell/Soundbells.mp3';
import useNotificationPolling from '../utils/useNotificationPolling';


const STATUS_MAP = {
  '-1': { label: 'Rejected', bg: 'from-red-500 to-red-600',         icon: XCircle,     body: 'Your purchase request has been rejected.' },
  '0':  { label: 'Draft',    bg: 'from-gray-500 to-gray-600',       icon: FileText,    body: 'Your purchase request is in draft.' },
  '1':  { label: 'Pending',  bg: 'from-amber-500 to-amber-600',     icon: Clock,       body: 'Your purchase request is pending approval.' },
  '2':  { label: 'Approved', bg: 'from-emerald-500 to-emerald-600', icon: CheckCircle, body: 'Your purchase request has been approved!' },
  '3':  { label: 'Issued',   bg: 'from-blue-500 to-blue-700',       icon: CheckCircle, body: 'Your purchase request has been issued!' },
};

export default function PRNotification() {
  const [alerts, setAlerts]             = useState([]);
  const [profileCache, setProfileCache] = useState({});
  const [notifSettings, setNotifSettings] = useState({ notice_sound: true, notice_view: true });

  const prevDataRef      = useRef(null);
  const lastUpdatedRef   = useRef(null);
  const userInteracted   = useRef(false);

  const currentUser   = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?.userId;

  // ── Fetch NotificationSettings once on mount ──
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${API_BASE_URL}api/NotificationSettings/user/${currentUserId}`)
      .then(r => r.json())
      .then(d => setNotifSettings(d))
      .catch(() => {});
  }, [currentUserId]);

  const isSoundOn = (s) => s === true || s === 1 || String(s).toLowerCase() === 'true';
  const isViewOn  = (s) => s === true || s === 1 || String(s).toLowerCase() === 'true';

  useEffect(() => {
    const handler = () => { userInteracted.current = true; };
    window.addEventListener('click',   handler);
    window.addEventListener('keydown', handler);
    window.addEventListener('scroll',  handler);
    return () => {
      window.removeEventListener('click',   handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('scroll',  handler);
    };
  }, []);

  const playSound = useCallback(() => {
    // ── Blocked if notice_sound is false ──
    if (!isSoundOn(notifSettings.notice_sound)) return;
    try {
      const audio = new Audio(bellSound);
      audio.volume = 0.7;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          const retry = () => {
            audio.play().catch(() => {});
            window.removeEventListener('click', retry);
          };
          window.addEventListener('click', retry, { once: true });
        });
      }
    } catch (err) {}
  }, [notifSettings.notice_sound]);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return;
    setProfileCache(prev => {
      if (prev[userId] !== undefined) return prev;
      return { ...prev, [userId]: null };
    });
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}/profile`);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          const pic  = data?.profileBase64 || null;
          setProfileCache(prev => ({ ...prev, [userId]: pic ? `data:image/jpeg;base64,${pic}` : null }));
        } else {
          const blob = await res.blob();
          const url  = URL.createObjectURL(blob);
          setProfileCache(prev => ({ ...prev, [userId]: url }));
        }
      }
    } catch {
      setProfileCache(prev => ({ ...prev, [userId]: null }));
    }
  }, []);

  const addAlert = useCallback((alert) => {
    // ── Blocked if notice_view is false ──
    if (!isViewOn(notifSettings.notice_view)) {
      // still play sound if sound is on
      playSound();
      return;
    }
    const alertWithId = { ...alert, uid: `${alert.prId}-${alert.type}-${Date.now()}` };
    setAlerts(prev => [...prev, alertWithId]);
    if (alert.userId) fetchProfile(alert.userId);
    playSound();
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.uid !== alertWithId.uid));
    }, 8000);
  }, [fetchProfile, playSound, notifSettings.notice_view]);

  const processData = useCallback((arr, isFirstLoad) => {
    if (isFirstLoad) {
      const snapshot = {};
      arr.forEach(pr => {
        snapshot[pr.id] = {
          status:       pr.status,
          creationDate: pr.creationDate,
        };
      });
      prevDataRef.current    = snapshot;
      const dates            = arr.map(p => new Date(p.creationDate || 0));
      lastUpdatedRef.current = dates.length ? Math.max(...dates) : 0;
      return;
    }

    const prev        = prevDataRef.current || {};
    const newSnapshot = {};
    let latestDate    = lastUpdatedRef.current || 0;

    arr.forEach(pr => {
      const creDate = new Date(pr.creationDate || 0).getTime();
      if (creDate > latestDate) latestDate = creDate;

      if (!prev[pr.id]) {
        if (String(pr.createdBy) !== String(currentUserId)) {
          addAlert({
            type:       'new',
            prId:       pr.id,
            userId:     pr.createdBy,
            title:      'New Purchase Request',
            name:       pr.requestor || 'Unknown',
            requestor:  pr.requestor || 'Unknown',
            controlNo:  pr.controlNumber || pr.prNo || 'N/A',
            department: pr.department || 'N/A',
            priority:   pr.priority || '',
            gradient:   'from-blue-600 to-blue-700',
            Icon:       ShoppingCart,
            message:    'submitted a new purchase request.',
          });
        }
      }

      if (prev[pr.id] && prev[pr.id].status !== pr.status) {
        if (String(pr.createdBy) === String(currentUserId)) {
          const s = STATUS_MAP[String(pr.status)];
          if (s) {
            addAlert({
              type:       'status',
              prId:       pr.id,
              userId:     pr.createdBy,
              title:      `PR ${s.label}`,
              name:       pr.requestor || 'Your request',
              requestor:  pr.requestor || 'Unknown',
              controlNo:  pr.controlNumber || pr.prNo || 'N/A',
              department: pr.department || 'N/A',
              priority:   pr.priority || '',
              gradient:   s.bg,
              Icon:       s.icon,
              message:    s.body,
            });
          }
        }
      }

      newSnapshot[pr.id] = {
        status:       pr.status,
        creationDate: pr.creationDate,
      };
    });

    prevDataRef.current    = newSnapshot;
    lastUpdatedRef.current = latestDate;
  }, [currentUserId, addAlert]);

  useNotificationPolling(`${API_BASE_URL}api/PR_Header`, currentUserId, async (arr) => {
    await processData(arr, prevDataRef.current === null);
  });

  const dismiss = (uid) => setAlerts(prev => prev.filter(a => a.uid !== uid));

  // ── If notice_view is off, render nothing ──
  if (!isViewOn(notifSettings.notice_view) || alerts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9998] flex flex-col gap-3 items-end pointer-events-none">
      {alerts.map((alert, idx) => {
        const Icon       = alert.Icon;
        const profilePic = profileCache[alert.userId];
        return (
          <div
            key={alert.uid}
            className="w-[340px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto"
            style={{ animation: 'slideUp 0.3s ease-out both', animationDelay: `${idx * 60}ms` }}
          >
            <div className={`bg-gradient-to-r ${alert.gradient} px-4 py-2.5 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-white text-xs font-bold uppercase tracking-wide">
                  {alert.title}
                </span>
                {alert.priority && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase
                    ${alert.priority === 'high'   ? 'bg-red-400/80 text-white' :
                      alert.priority === 'medium' ? 'bg-yellow-400/80 text-white' :
                                                    'bg-white/20 text-white'}`}>
                    {alert.priority}
                  </span>
                )}
              </div>
              <button
                onClick={() => dismiss(alert.uid)}
                className="p-1 bg-white/10 hover:bg-white/25 rounded-lg transition-all"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>

            <div className="px-4 py-3.5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {profilePic ? (
                    <img
                      src={profilePic}
                      alt="profile"
                      className="w-10 h-10 rounded-xl object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-400" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <User className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                    <span className="font-bold text-gray-900 text-sm truncate">{alert.requestor}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1.5">{alert.message}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3 text-blue-400" />
                      <span className="font-mono font-bold text-blue-700 text-xs">{alert.controlNo}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-400 text-xs truncate">{alert.department}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="h-1 bg-gray-100">
              <div
                className={`h-full bg-gradient-to-r ${alert.gradient} rounded-full`}
                style={{ animation: 'shrink 8s linear forwards' }}
              />
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
}
